import { $api } from "@/kibi/core/utils/api";
import type { InjectionKey } from "vue";
import type {
  CreateRoleRequestDTO,
  GroupedPermissionsDTO,
  RoleDTO,
  RolePermissionsDTO,
  UpdateRoleRequestDTO,
} from "../models/role_model";

export interface RoleDataSource {
  getRoles(): Promise<RoleDTO[]>;
  createRole(request: CreateRoleRequestDTO): Promise<RoleDTO>;
  updateRole(id: string, request: UpdateRoleRequestDTO): Promise<RoleDTO>;
  deleteRole(id: string): Promise<void>;
  getAllPermissions(): Promise<GroupedPermissionsDTO[]>;
  getRolePermissions(roleId: string): Promise<string[]>;
  assignPermissions(roleId: string, request: RolePermissionsDTO): Promise<void>;
}

export const roleDataSourceKey: InjectionKey<RoleDataSource> =
  Symbol("RoleDataSource");

export class RoleDataSourceImpl implements RoleDataSource {
  async getRoles(): Promise<RoleDTO[]> {
    const response = await $api<{ data: RoleDTO[] }>("/accounts/roles", {
      method: "GET",
    });
    return response.data;
  }

  async createRole(request: CreateRoleRequestDTO): Promise<RoleDTO> {
    const response = await $api<{ data: RoleDTO }>("/accounts/roles", {
      method: "POST",
      body: request,
    });
    return response.data;
  }

  async updateRole(
    id: string,
    request: UpdateRoleRequestDTO,
  ): Promise<RoleDTO> {
    const response = await $api<{ data: RoleDTO }>(`/accounts/roles/${id}`, {
      method: "PUT",
      body: request,
    });
    return response.data;
  }

  async deleteRole(id: string): Promise<void> {
    await $api(`/accounts/roles/${id}`, {
      method: "DELETE",
    });
  }

  async getAllPermissions(): Promise<GroupedPermissionsDTO[]> {
    const response = await $api<{ data: GroupedPermissionsDTO[] }>(
      "/accounts/permissions",
      {
        method: "GET",
      },
    );
    return response.data;
  }

  async getRolePermissions(roleId: string): Promise<string[]> {
    const response = await $api<{ data: string[] }>(
      `/accounts/roles/${roleId}/permissions`,
      {
        method: "GET",
      },
    );
    return response.data;
  }

  async assignPermissions(
    roleId: string,
    request: RolePermissionsDTO,
  ): Promise<void> {
    await $api(`/accounts/roles/${roleId}/permissions`, {
      method: "PUT",
      body: request,
    });
  }
}
